/**
 * The api↔control transport for the preparation lane (T1b plan F3, S4).
 *
 * These are internal control-plane shapes, not product contracts: the api asks control to enqueue one
 * already-built preparation request for a device, and later to read that request's status. Control
 * answers with typed outcomes so the api can record WHY a device refused (`agent_capability_missing`,
 * `input_preparation_request_conflict`, …) instead of a blanket 502.
 */
import { z } from "zod";
import { AgentInputPreparationPayloadSchema, InputPreparationReadbackSchema } from "@byok-sdk/protocol";

const TenantRefSchema = z.string().min(1).max(200);
const DeviceIdSchema = z.string().min(1).max(200);

export const PrivateAgentChatPrepareRequestSchema = z
  .object({ tenantRef: TenantRefSchema, deviceId: DeviceIdSchema, request: AgentInputPreparationPayloadSchema })
  .strict();
export type PrivateAgentChatPrepareRequest = z.infer<typeof PrivateAgentChatPrepareRequestSchema>;

export const PrivateAgentChatPrepareStatusRequestSchema = z
  .object({ tenantRef: TenantRefSchema, deviceId: DeviceIdSchema,
    // The SDK's readback key is the payload's own agentRef OBJECT (not the bare agent id).
    agentRef: AgentInputPreparationPayloadSchema.shape.agentRef, requestId: z.string().min(1).max(200) })
  .strict();
export type PrivateAgentChatPrepareStatusRequest = z.infer<typeof PrivateAgentChatPrepareStatusRequestSchema>;

/**
 * The closed refusal vocabulary the api records. Each code is the SDK's own (mapped, never invented);
 * the split that matters is RETRYABILITY, and it is decided here rather than by the api guessing:
 *
 * - `input_preparation_unavailable` — the SDK could not be reached at all (a non-`ByokCloudError`
 *   throw: transport, timeout). Transient, so the api retries inside the attempt.
 * - `agent_capability_missing` — the device never advertised the capability. The api records
 *   `preparation_device_uncapable` and re-sends the same body until the attempt's deadline.
 * - `input_preparation_request_conflict`, `input_preparation_invalid_request`,
 *   `input_preparation_receipt_invalid`, `input_preparation_unrecognized_error` — deterministic
 *   refusals: the same bytes would be refused again, so the api stops the Turn observably. The last
 *   one is the fail-closed bucket for a code this contract does not know, and it carries the SDK's
 *   own name in `sdkCode` so the Host's log can name what it refused.
 */
export const PrivateAgentChatPreparationRejectionCodeSchema = z.enum([
  "agent_capability_missing",
  "input_preparation_request_conflict",
  "input_preparation_invalid_request",
  "input_preparation_receipt_invalid",
  "input_preparation_unrecognized_error",
  "input_preparation_unavailable",
]);
export type PrivateAgentChatPreparationRejectionCode = z.infer<typeof PrivateAgentChatPreparationRejectionCodeSchema>;

export const PrivateAgentChatPrepareResponseSchema = z.union([
  z.object({ status: z.literal("accepted"), requestId: z.string().min(1),
    readback: InputPreparationReadbackSchema }).strict(),
  z.object({ status: z.literal("rejected"), code: PrivateAgentChatPreparationRejectionCodeSchema,
    message: z.string(), sdkCode: z.string().min(1).max(200).optional() }).strict(),
]);
export type PrivateAgentChatPrepareResponse = z.infer<typeof PrivateAgentChatPrepareResponseSchema>;

/**
 * `absent` is the SDK's own answer when it holds NO record for this request
 * (`getInputPreparationStatus` resolved `undefined`). It is not decoration: it is the ONLY state in
 * which the Host may enqueue, so relaying it as `pending` would make "enqueue only when the request
 * is unknown" unimplementable and would re-send a request the device is already working on.
 */
export const PrivateAgentChatPrepareStatusResponseSchema = z.union([
  z.object({ status: z.literal("absent") }).strict(),
  z.object({ status: z.literal("pending") }).strict(),
  z.object({ status: z.literal("completed"), readback: InputPreparationReadbackSchema }).strict(),
  z.object({ status: z.literal("rejected"), code: PrivateAgentChatPreparationRejectionCodeSchema,
    message: z.string(), sdkCode: z.string().min(1).max(200).optional() }).strict(),
]);
export type PrivateAgentChatPrepareStatusResponse = z.infer<typeof PrivateAgentChatPrepareStatusResponseSchema>;
